import { useState, useEffect, useRef, useMemo, FormEvent } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { every, useRefresh } from '@signalvote/data';
import { ApiError } from '@signalvote/api-client';
import { CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { useCampaignStore } from '@/stores';
import { useMayManageCampaign } from '@/hooks';
import { billingStatus, useBillingConfig, useBillingStatus, useCreatePortal, useSubscribe } from '@/hooks/queries';
import { queryKeys } from '@/lib/queryKeys';
import { campaignDaysUntil } from '@/lib/calendarUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ErrorCode, type SubscriptionStatus } from '@signalvote/shared';

const statusConfig: Record<SubscriptionStatus, { label: string; color: string }> = {
  Active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  Trialing: { label: 'Trial', color: 'bg-blue-100 text-blue-800' },
  PastDue: { label: 'Past Due', color: 'bg-yellow-100 text-yellow-800' },
  Unpaid: { label: 'Unpaid', color: 'bg-red-100 text-red-800' },
  Canceled: { label: 'Canceled', color: 'bg-red-100 text-red-800' },
};

function formatDate(iso: string | undefined, timeZoneId: string | undefined): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: timeZoneId,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function trialDaysLeft(trialEndsAt: string | undefined, timeZoneId: string | undefined): string | null {
  if (!trialEndsAt) return null;
  const days = campaignDaysUntil(trialEndsAt, timeZoneId);
  if (days <= 0) return 'Expires today';
  return `${days} day${days === 1 ? '' : 's'} left`;
}

function formatPrice(amount: number, currency: string, interval: string): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount / 100);
  return `${formatted}/${interval === 'month' ? 'mo' : interval}`;
}

/** Inline Stripe payment form rendered inside the SubscriptionCard. */
function InlineCheckoutForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setError(null);
    setIsConfirming(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/subscribe/complete',
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message ?? 'Payment failed. Please try again.');
      } else {
        onSuccess();
      }
    } catch {
      setError('Failed to process payment. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button type="submit" disabled={!stripe || isConfirming} className="w-full" size="sm">
        {isConfirming ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="mr-2 h-4 w-4" />
            Subscribe
          </>
        )}
      </Button>
    </form>
  );
}

/** Inline subscribe section: creates Stripe subscription + shows payment form. */
function InlineSubscribeSection({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const configRead = useBillingConfig();
  const config = configRead.status === 'ready' ? configRead.data : undefined;
  const refresh = useRefresh();
  const { mutateAsync: subscribe, isPending, data: subscribeData } = useSubscribe();
  const [error, setError] = useState<string | null>(null);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const nothingDue = !!subscribeData && !subscribeData.clientSecret;

  const stripePromise = useMemo(
    () => (config ? loadStripe(config.publishableKey) : null),
    [config?.publishableKey],
  );

  // Auto-create subscription to get clientSecret.
  // Fires once per mount: a failed attempt must not retry on its own (a 409 would loop).
  const subscribeCalledRef = useRef(false);
  useEffect(() => {
    if (config && !subscribeData && !isPending && !subscribeCalledRef.current) {
      subscribeCalledRef.current = true;
      subscribe().catch((err) => {
        if (err instanceof ApiError && err.code === ErrorCode.BillingAlreadySubscribed) {
          setAlreadySubscribed(true);
          void refresh(billingStatus, every);
        } else {
          setError('Failed to set up subscription. Please try again.');
        }
      });
    }
  }, [config, subscribeData, isPending, subscribe, refresh]);

  useEffect(() => {
    if (nothingDue) onSuccess();
  }, [nothingDue, onSuccess]);

  if (alreadySubscribed) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">This campaign already has a subscription.</p>
        <Button variant="outline" size="sm" onClick={onCancel}>Back</Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600">{error}</p>
        <Button variant="outline" size="sm" onClick={onCancel}>Back</Button>
      </div>
    );
  }

  if (isPending || !subscribeData || nothingDue || !stripePromise) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Setting up payment...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Elements stripe={stripePromise} options={{ clientSecret: subscribeData.clientSecret }}>
        <InlineCheckoutForm onSuccess={onSuccess} />
      </Elements>
      <button
        type="button"
        onClick={onCancel}
        className="text-sm text-muted-foreground hover:text-foreground underline"
      >
        Cancel
      </button>
    </div>
  );
}

export function SubscriptionCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const selectCampaign = useCampaignStore((s) => s.selectCampaign);

  const mayManageBilling = useMayManageCampaign();
  const queryClient = useQueryClient();
  const refresh = useRefresh();
  const statusRead = useBillingStatus();
  const status = statusRead.status === 'ready' ? statusRead.data : undefined;
  const configRead = useBillingConfig();
  const config = configRead.status === 'ready' ? configRead.data : undefined;
  const { mutate: openPortal, isPending } = useCreatePortal();
  const [showCheckout, setShowCheckout] = useState(false);

  const handleCheckoutSuccess = () => {
    // 1. Update Zustand immediately so UI reflects Active
    if (selectedCampaign) {
      selectCampaign({ ...selectedCampaign, subscriptionStatus: 'Active' });
    }
    // 2. Close the checkout form
    setShowCheckout(false);
    // 3. Delay the refresh to give the webhook time to process, so the refetch picks up Active
    //    instead of overwriting the optimistic update. Stripe's webhook sends this page no write set.
    setTimeout(() => {
      void refresh(billingStatus, every);
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.my() });
    }, 3000);
  };

  if (!selectedCampaign) return null;

  const sub = status ?? {
    subscriptionStatus: selectedCampaign.subscriptionStatus,
    trialEndsAt: selectedCampaign.trialEndsAt,
    subscriptionCurrentPeriodEnd: undefined,
    subscriptionCancelAtPeriodEnd: false,
    hasStripeSubscription: false,
  };

  const { label, color } = statusConfig[sub.subscriptionStatus] ?? statusConfig.Trialing;
  const hasStripeSubscription = !!sub.hasStripeSubscription;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          Subscription
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {config && (
            <>
              <span className="text-muted-foreground">Plan</span>
              <span>
                {config.discount ? (
                  <>
                    {formatPrice(config.discount.netAmount, config.currency, config.interval)}
                    <span className="ml-2 text-muted-foreground line-through">
                      {formatPrice(config.amount, config.currency, config.interval)}
                    </span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({config.discount.name})
                    </span>
                  </>
                ) : (
                  formatPrice(config.amount, config.currency, config.interval)
                )}
              </span>
            </>
          )}

          <span className="text-muted-foreground">Status</span>
          <span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
              {label}
            </span>
          </span>

          {sub.subscriptionStatus === 'Trialing' && sub.trialEndsAt && (
            <>
              <span className="text-muted-foreground">Trial ends</span>
              <span>
                {formatDate(sub.trialEndsAt, selectedCampaign.timeZoneId)}
                {' '}
                <span className="text-muted-foreground">
                  ({trialDaysLeft(sub.trialEndsAt, selectedCampaign.timeZoneId)})
                </span>
              </span>
            </>
          )}

          {sub.subscriptionCancelAtPeriodEnd && sub.subscriptionCurrentPeriodEnd && (
            <>
              <span className="text-muted-foreground">Cancels on</span>
              <span className="text-red-600">{formatDate(sub.subscriptionCurrentPeriodEnd, selectedCampaign.timeZoneId)}</span>
            </>
          )}

          {!sub.subscriptionCancelAtPeriodEnd && sub.subscriptionCurrentPeriodEnd && sub.subscriptionStatus === 'Active' && (
            <>
              <span className="text-muted-foreground">Next billing</span>
              <span>{formatDate(sub.subscriptionCurrentPeriodEnd, selectedCampaign.timeZoneId)}</span>
            </>
          )}
        </div>

        {!mayManageBilling ? (
          <p className="text-sm text-muted-foreground">
            Only the campaign owner or a full admin can change billing.
          </p>
        ) : showCheckout ? (
          <InlineSubscribeSection onSuccess={handleCheckoutSuccess} onCancel={() => setShowCheckout(false)} />
        ) : hasStripeSubscription ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openPortal({ returnUrl: window.location.href })}
            disabled={isPending}
            className="mt-2"
          >
            <ExternalLink className="mr-2 h-3.5 w-3.5" />
            {isPending ? 'Loading...' : 'Manage Billing'}
          </Button>
        ) : (
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCheckout(true)}
            className="mt-2"
          >
            Subscribe
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
